import type { CurrentOrganization, UpdateOrganizationPayload } from '@/types/organization.types'
import type { OrganizationFormValues } from '@/features/organization/schemas/organizationForm.schema'

export const DEFAULT_ATTENDANCE = {
  minimumHoursForPresent: 1,
  lateGracePeriodMinutes: 5,
  autoDeductLateMinutes: true,
}
export const DEFAULT_BREAK = {
  defaultPaidBreakMinutes: 30,
  defaultUnpaidBreakMinutes: 0,
  autoDeductExcessBreak: true,
}
export const DEFAULT_AGENT = {
  screenshotEnabled: true,
  screenshotIntervalMinutes: 5,
  screenshotMode: 'random' as const,
  inactivityThresholdMinutes: 5,
  inactivityAlertEnabled: true,
}
export const DEFAULT_SHIFT_ALERT = {
  preShiftAlertMinutes: 30,
  reminderIntervalMinutes: 5,
  maxMissedReminders: 3,
  escalateToAdmin: true,
  suppressForLeave: true,
}
export const DEFAULT_PAYROLL = {
  payrollCycleType: 'monthly' as const,
  requireApproval: true,
  lockAfterApproval: true,
}
export const DEFAULT_LEAVE = {
  requireHRApproval: true,
  requireAdminFinalApproval: true,
  allowNegativeBalance: false,
}
export const DEFAULT_NOTIFICATION = {
  emailEnabled: true,
  inAppEnabled: true,
}
export const DEFAULT_CHAT = {
  directMessagesEnabled: true,
  directMessageScope: 'within_scope' as const,
  allowAllRolesToCreateChannels: false,
  allowVoiceNotes: false,
  allowManagementToStaffDirectMessages: true,
  allowStaffToStaffDirectMessages: false,
  allowStaffToManagementDirectMessages: false,
}

export function formValuesFromOrganization(org: CurrentOrganization): OrganizationFormValues {
  const settings = org.settings
  return {
    name: org.name ?? '',
    slug: org.slug ?? '',
    registrationNumber: org.registrationNumber ?? '',
    taxId: org.taxId ?? '',
    industry: org.industry ?? '',
    contactEmail: org.contactEmail ?? '',
    contactPhone: org.contactPhone ?? '',
    website: org.website ?? '',
    street: org.address?.street ?? '',
    city: org.address?.city ?? '',
    state: org.address?.state ?? '',
    country: org.address?.country ?? '',
    postalCode: org.address?.postalCode ?? '',
    primaryColor: org.branding?.primaryColor ?? '',
    footerText: org.branding?.footerText ?? '',
    termsUrl: org.branding?.termsUrl ?? '',
    privacyUrl: org.branding?.privacyUrl ?? '',
    status: org.status,
    defaultCurrency: settings?.defaultCurrency ?? '',
    supportedCurrencies: settings?.supportedCurrencies ?? [],
    timezone: settings?.timezone ?? '',
    dateFormat: settings?.dateFormat ?? '',
    companyCodePrefix: settings?.companyCodePrefix ?? 'CO',
    workWeek: settings?.workWeek ?? [],
    attendanceSettings: {
      ...DEFAULT_ATTENDANCE,
      ...settings?.attendanceSettings,
    },
    breakSettings: {
      ...DEFAULT_BREAK,
      ...settings?.breakSettings,
    },
    agentSettings: {
      ...DEFAULT_AGENT,
      ...settings?.agentSettings,
      screenshotMode:
        settings?.agentSettings?.screenshotMode === 'fixed' ||
        settings?.agentSettings?.screenshotMode === 'random'
          ? settings.agentSettings.screenshotMode
          : DEFAULT_AGENT.screenshotMode,
    },
    shiftAlertSettings: {
      ...DEFAULT_SHIFT_ALERT,
      ...settings?.shiftAlertSettings,
    },
    payrollSettings: {
      ...DEFAULT_PAYROLL,
      ...settings?.payrollSettings,
      payrollCycleType:
        settings?.payrollSettings?.payrollCycleType === 'weekly' ||
        settings?.payrollSettings?.payrollCycleType === 'biweekly' ||
        settings?.payrollSettings?.payrollCycleType === 'monthly'
          ? settings.payrollSettings.payrollCycleType
          : DEFAULT_PAYROLL.payrollCycleType,
    },
    leaveSettings: {
      ...DEFAULT_LEAVE,
      ...settings?.leaveSettings,
    },
    notificationSettings: {
      ...DEFAULT_NOTIFICATION,
      ...settings?.notificationSettings,
    },
    chatSettings: {
      ...DEFAULT_CHAT,
      ...settings?.chatSettings,
      directMessageScope:
        settings?.chatSettings?.directMessageScope === 'all_organization_members' ||
        settings?.chatSettings?.directMessageScope === 'within_scope'
          ? settings.chatSettings.directMessageScope
          : DEFAULT_CHAT.directMessageScope,
    },
  }
}

function finiteNumber(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined
}

export function formValuesToPayload(values: OrganizationFormValues): UpdateOrganizationPayload {
  const payload: UpdateOrganizationPayload = {
    name: values.name || undefined,
    // The slug is not sent: only KAAMCO support can change the workspace address.
    registrationNumber: values.registrationNumber || undefined,
    taxId: values.taxId || undefined,
    industry: values.industry || undefined,
    contactEmail: values.contactEmail || undefined,
    contactPhone: values.contactPhone || undefined,
    website: values.website || undefined,
    status: values.status,
  }
  if (
    values.street ||
    values.city ||
    values.state ||
    values.country ||
    values.postalCode
  ) {
    payload.address = {
      street: values.street || undefined,
      city: values.city || undefined,
      state: values.state || undefined,
      country: values.country || undefined,
      postalCode: values.postalCode || undefined,
    }
  }
  // Sent even when empty, because empty is how these get cleared.
  payload.branding = {
    footerText: values.footerText?.trim() ?? '',
    termsUrl: values.termsUrl?.trim() ?? '',
    privacyUrl: values.privacyUrl?.trim() ?? '',
  }
  if (values.primaryColor) {
    payload.branding.primaryColor = values.primaryColor
  }
  const settingsPayload: UpdateOrganizationPayload['settings'] = {}
  if (values.defaultCurrency?.trim()) settingsPayload.defaultCurrency = values.defaultCurrency.trim()
  if (values.supportedCurrencies?.length) settingsPayload.supportedCurrencies = values.supportedCurrencies
  if (values.timezone?.trim()) settingsPayload.timezone = values.timezone.trim()
  if (values.dateFormat?.trim()) settingsPayload.dateFormat = values.dateFormat.trim()
  if (values.companyCodePrefix?.trim()) {
    settingsPayload.companyCodePrefix = values.companyCodePrefix.trim().toUpperCase()
  }
  if (values.workWeek?.length) settingsPayload.workWeek = values.workWeek
  if (values.attendanceSettings)
    settingsPayload.attendanceSettings = {
      minimumHoursForPresent: finiteNumber(values.attendanceSettings.minimumHoursForPresent) ?? DEFAULT_ATTENDANCE.minimumHoursForPresent,
      lateGracePeriodMinutes: finiteNumber(values.attendanceSettings.lateGracePeriodMinutes) ?? DEFAULT_ATTENDANCE.lateGracePeriodMinutes,
      autoDeductLateMinutes: values.attendanceSettings.autoDeductLateMinutes ?? DEFAULT_ATTENDANCE.autoDeductLateMinutes,
    }
  if (values.breakSettings)
    settingsPayload.breakSettings = {
      defaultPaidBreakMinutes: finiteNumber(values.breakSettings.defaultPaidBreakMinutes) ?? DEFAULT_BREAK.defaultPaidBreakMinutes,
      defaultUnpaidBreakMinutes: finiteNumber(values.breakSettings.defaultUnpaidBreakMinutes) ?? DEFAULT_BREAK.defaultUnpaidBreakMinutes,
      autoDeductExcessBreak: values.breakSettings.autoDeductExcessBreak ?? DEFAULT_BREAK.autoDeductExcessBreak,
    }
  if (values.agentSettings)
    settingsPayload.agentSettings = {
      screenshotEnabled: values.agentSettings.screenshotEnabled ?? DEFAULT_AGENT.screenshotEnabled,
      screenshotIntervalMinutes: finiteNumber(values.agentSettings.screenshotIntervalMinutes) ?? DEFAULT_AGENT.screenshotIntervalMinutes,
      screenshotMode: values.agentSettings.screenshotMode ?? DEFAULT_AGENT.screenshotMode,
      inactivityThresholdMinutes: finiteNumber(values.agentSettings.inactivityThresholdMinutes) ?? DEFAULT_AGENT.inactivityThresholdMinutes,
      inactivityAlertEnabled: values.agentSettings.inactivityAlertEnabled ?? DEFAULT_AGENT.inactivityAlertEnabled,
    }
  if (values.shiftAlertSettings)
    settingsPayload.shiftAlertSettings = {
      preShiftAlertMinutes: finiteNumber(values.shiftAlertSettings.preShiftAlertMinutes) ?? DEFAULT_SHIFT_ALERT.preShiftAlertMinutes,
      reminderIntervalMinutes: finiteNumber(values.shiftAlertSettings.reminderIntervalMinutes) ?? DEFAULT_SHIFT_ALERT.reminderIntervalMinutes,
      maxMissedReminders: finiteNumber(values.shiftAlertSettings.maxMissedReminders) ?? DEFAULT_SHIFT_ALERT.maxMissedReminders,
      escalateToAdmin: values.shiftAlertSettings.escalateToAdmin ?? DEFAULT_SHIFT_ALERT.escalateToAdmin,
      suppressForLeave: values.shiftAlertSettings.suppressForLeave ?? DEFAULT_SHIFT_ALERT.suppressForLeave,
    }
  if (values.payrollSettings)
    settingsPayload.payrollSettings = {
      payrollCycleType: values.payrollSettings.payrollCycleType ?? DEFAULT_PAYROLL.payrollCycleType,
      requireApproval: values.payrollSettings.requireApproval ?? DEFAULT_PAYROLL.requireApproval,
      lockAfterApproval: values.payrollSettings.lockAfterApproval ?? DEFAULT_PAYROLL.lockAfterApproval,
    }
  if (values.leaveSettings)
    settingsPayload.leaveSettings = {
      requireHRApproval: values.leaveSettings.requireHRApproval ?? DEFAULT_LEAVE.requireHRApproval,
      requireAdminFinalApproval: values.leaveSettings.requireAdminFinalApproval ?? DEFAULT_LEAVE.requireAdminFinalApproval,
      allowNegativeBalance: values.leaveSettings.allowNegativeBalance ?? DEFAULT_LEAVE.allowNegativeBalance,
    }
  if (values.notificationSettings)
    settingsPayload.notificationSettings = {
      emailEnabled: values.notificationSettings.emailEnabled ?? DEFAULT_NOTIFICATION.emailEnabled,
      inAppEnabled: values.notificationSettings.inAppEnabled ?? DEFAULT_NOTIFICATION.inAppEnabled,
    }
  if (values.chatSettings)
    settingsPayload.chatSettings = {
      directMessagesEnabled:
        values.chatSettings.directMessagesEnabled ?? DEFAULT_CHAT.directMessagesEnabled,
      directMessageScope:
        values.chatSettings.directMessageScope ?? DEFAULT_CHAT.directMessageScope,
      allowAllRolesToCreateChannels:
        values.chatSettings.allowAllRolesToCreateChannels ??
        DEFAULT_CHAT.allowAllRolesToCreateChannels,
      allowVoiceNotes: values.chatSettings.allowVoiceNotes ?? DEFAULT_CHAT.allowVoiceNotes,
      allowManagementToStaffDirectMessages:
        values.chatSettings.allowManagementToStaffDirectMessages ??
        DEFAULT_CHAT.allowManagementToStaffDirectMessages,
      allowStaffToStaffDirectMessages:
        values.chatSettings.allowStaffToStaffDirectMessages ??
        DEFAULT_CHAT.allowStaffToStaffDirectMessages,
      allowStaffToManagementDirectMessages:
        values.chatSettings.allowStaffToManagementDirectMessages ??
        DEFAULT_CHAT.allowStaffToManagementDirectMessages,
    }
  if (Object.keys(settingsPayload).length > 0) payload.settings = settingsPayload
  return payload
}

export function normalizeHex(value: string): string {
  const trimmed = value.trim()
  if (!trimmed) return ''
  if (trimmed.startsWith('#')) return trimmed
  return `#${trimmed}`
}
